'use client';

import { useEffect, useRef, useState, type ReactNode } from 'react';
import Link from 'next/link';
import UiIcon from '@/components/ui/UiIcon';
import Button from '@/components/ui/Button';
import { useAdminAccess } from '@/hooks/useAdminAccess';
import { IconBriefcase, IconChevronDown, IconFlask, IconMenu, IconNewspaper, IconSettings, IconUser, IconMessageCircle, IconRocket } from '@/components/ui/MonoIcons';

const ITEMS = [
  { href: '/', label: 'Главная', icon: <UiIcon name="Cube" className="w-4 h-4" /> },
  { href: '/home-preview-3d', label: 'Прежняя главная', icon: <UiIcon name="House" className="w-4 h-4" /> },
  { href: '/home-preview', label: 'Концепт главной', icon: <UiIcon name="Compass" className="w-4 h-4" /> },
  { href: '/news', label: 'Новости', icon: <IconNewspaper className="w-4 h-4" /> },
  { href: '/projects', label: 'Проекты', icon: <IconRocket className="w-4 h-4" /> },
  { href: '/vacancies', label: 'Вакансии', icon: <IconBriefcase className="w-4 h-4" /> },
  // В проекте чат находится в /messages; /chat/* не существует и ломает роутер.
  // Комментарий на русском: брендируем пункт меню “Чат” как STEM (шрифт зададим в рендере ниже).
  { href: '/messages', label: 'S T E M', icon: <IconMessageCircle className="w-4 h-4" /> },
    { href: '/games', label: 'Игры', icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg> },
  { href: '/about', label: 'О лаборатории', icon: <IconFlask className="w-4 h-4" /> },
] as const;

export default function SiteMenu({ compact = false, align = 'end', actions }: { compact?: boolean; align?: 'start' | 'end'; actions?: ReactNode }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  const { isAuthed, hydrated, hasAdminAccess, canViewHomePreviews } = useAdminAccess(open);
  const requestNavigate = (href: string) => {
    if (typeof window === 'undefined') return false;
    if (!(window as any).__newsEditorActive) return false;
    window.dispatchEvent(new CustomEvent('news_editor:navigate', { detail: { href } }));
    return true;
  };

  // Закрываем меню по клику вне/ESC — чтобы не залипало на мобилках и в десктопе.
  useEffect(() => {
    if (!open) return;

    const onPointerDown = (e: PointerEvent) => {
      const node = ref.current;
      if (node && !node.contains(e.target as Node)) setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        ref.current?.querySelector('button')?.focus();
      }
    };

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <Button
        variant="ghost"
        size="sm"
        // Комментарий на русском: делаем тонкую оранжевую обводку на всех платформах.
        className="rounded-full border border-site-warning/35 bg-site-bg/15 hover:bg-site-surface/35 hover:border-site-warning/55"
        onClick={() => setOpen(v => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Открыть меню"
      >
        <IconMenu className="w-4 h-4" />
        <span className={compact ? 'hidden lg:inline' : 'hidden sm:inline'}>Меню</span>
        <span className={`hidden text-site-muted transition-transform ${compact ? 'lg:inline-flex' : 'sm:inline-flex'} ${open ? 'rotate-180' : ''}`}>
          <IconChevronDown className="w-4 h-4" />
        </span>
      </Button>

      <div
        role="menu"
        aria-label="Меню сайта"
        aria-hidden={!open}
        inert={!open}
        className={`absolute ${align === 'start' ? 'left-0 right-auto' : 'right-0 left-auto sm:right-auto sm:left-0'} ${compact ? 'max-sm:fixed max-sm:left-4 max-sm:right-4 max-sm:w-auto' : ''} z-50 mt-3 w-64 overflow-hidden rounded-2xl border border-site-border bg-site-bg/95 backdrop-blur-md shadow-[0_20px_60px_rgba(0,0,0,0.45)] transition-all duration-200 ${
          open ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 -translate-y-1 pointer-events-none'
        }`}
      >
        {isAuthed && (
          <>
            <Link
              prefetch={false}
              href="/profile"
              role="menuitem"
              className="flex items-center gap-2 px-4 py-3 text-site-text hover:bg-site-surface/45 hover:text-site-text transition-colors"
              onClick={(e) => {
                if (requestNavigate('/profile')) {
                  e.preventDefault();
                  setOpen(false);
                  return;
                }
                setOpen(false);
              }}
            >
              <IconUser className="w-4 h-4 text-site-secondary" />
              <span>Мой профиль</span>
            </Link>

            {hasAdminAccess && (
              <Link
                prefetch={false}
                href="/admin"
                role="menuitem"
                className="flex items-center gap-2 px-4 py-3 text-site-text hover:bg-site-surface/45 hover:text-site-text transition-colors"
                onClick={(e) => {
                  if (requestNavigate('/admin')) {
                    e.preventDefault();
                    setOpen(false);
                    return;
                  }
                  setOpen(false);
                }}
              >
                <IconSettings className="w-4 h-4 text-site-secondary" />
                <span>Админ-панель</span>
              </Link>
            )}

            <div className="h-px bg-site-raised/40" />
          </>
        )}

        {hydrated && !isAuthed && (
          <Link
            prefetch={false}
            href="/login"
            role="menuitem"
            className="flex items-center gap-2 px-4 py-3 text-site-text hover:bg-site-surface/45 hover:text-site-text transition-colors"
            onClick={(e) => {
              if (requestNavigate('/login')) e.preventDefault();
              setOpen(false);
            }}
          >
            <IconUser className="w-4 h-4 text-site-secondary" />
            <span>Войти</span>
          </Link>
        )}

        {ITEMS.filter(i => canViewHomePreviews || !['/home-preview-3d', '/home-preview'].includes(i.href)).map((i) => (
          <Link
            key={i.href}
            prefetch={false}
            href={i.href}
            role="menuitem"
            className="flex items-center gap-2 px-4 py-3 text-site-text hover:bg-site-surface/45 hover:text-site-text transition-colors"
            onClick={(e) => {
              if (requestNavigate(i.href)) {
                e.preventDefault();
                setOpen(false);
                return;
              }
              setOpen(false);
            }}
          >
            <span className="text-site-secondary">{i.icon}</span>
            <span
              // Комментарий на русском: для пункта STEM используем фирменный шрифт.
              style={i.href === '/messages' ? { fontFamily: 'GlitchGoblin, system-ui, sans-serif', letterSpacing: '0.14em' } : undefined}
            >
              {i.label}
            </span>
          </Link>
        ))}
        {actions && <div className="flex flex-wrap items-center gap-2 border-t border-site-border p-3">{actions}</div>}
      </div>
    </div>
  );
}
